<template>

  <BasicLayout>

    <div>

      <!-- SEARCH -->
      <Search :searching="searching" @search="search" @closeSearch="searching = false" @clearSearch="clearSearch" />

      <div class="elevation-2">

        <!-- TOOLBAR -->
        <v-toolbar flat="flat">
          <v-toolbar-title>Competitions</v-toolbar-title>
          <v-flex class="ml-5" md3="md3">
            <v-text-field v-show="!loading && !searching" v-model="quickSearchID" placeholder="Search by ID"
              :clearable="true"></v-text-field>
          </v-flex>
          <v-spacer></v-spacer>
          <v-icon class="mr-2" v-if="!searching" title="Show the search" @click="searching = true">mdi-magnify</v-icon>
          <v-icon title="Create club" @click="dialog = true">mdi-plus</v-icon>
        </v-toolbar>

        <!-- TABLE -->
        <Table :headers="headers" :items="items" :pagination="pagination" :paginationOptions="paginationOptions"
          :totalItems="pagination.totalItems" :loading="loading" @paginationUpdate="paginationUpdate" @edit="toggleEdit"
          @del="del" />

        <!-- CREATE / EDIT -->
        <Dialog :dialog="dialog" :dialogState="dialogState" :currentEditedCompetition="editedItem"
          :errorMessage="errorMessage" @dialogClosed="close" @createCompetition="createCompetition"
          @editCompetition="editCompetition" />

      </div>
    </div>

    <!-- LOADER -->
    <LoadingPopup :displayed="loaderDisplayed" :title="loaderTitle" :text="loaderText" />

  </BasicLayout>

</template>

<script>

import BasicLayout from '../layouts/BasicLayout'
import Search from '../components/competitions/Search'
import Table from '../components/competitions/Table'
import Dialog from '../components/competitions/Dialog'
import LoadingPopup from '../components/common/LoadingPopup'

import env from '@/tools/env'
import { getSportInfos } from '../helpers/get-sport-infos'

export default {
  name: 'ClubsPage',
  components: {
    BasicLayout,
    Search,
    Table,
    Dialog,
    LoadingPopup
  },
  data () {
    return {
      env,

      loading: false,

      items: [],

      headers: [
        {
          text: 'ID',
          value: 'id',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Logo',
          value: 'logoURL',
          sortable: false,
          width: '60px',
          countries: ['FR', 'US']
        },
        {
          text: 'Name',
          value: 'name',
          sortable: false,
          width: '200px',
          countries: ['FR', 'US']
        },
        {
          text: 'Type',
          value: 'isTournament',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Federation',
          value: 'federationID',
          sortable: false,
          width: '150px',
          countries: ['FR', 'US']
        },
        {
          text: 'Region',
          value: 'regionAlias',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Category',
          value: 'categoryID',
          sortable: false,
          width: '200px',
          countries: ['FR', 'US']
        },
        {
          text: 'Season',
          value: 'seasonID',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Start Date',
          value: 'startDate',
          sortable: false,
          width: '200px',
          countries: ['FR', 'US']
        },
        {
          text: 'End Date',
          value: 'endDate',
          sortable: false,
          width: '200px',
          countries: ['FR', 'US']
        }
      ],

      pagination: {
        page: 1,
        rowsPerPage: 10,
        totalItems: 0
      },
      paginationOptions: [10, 50, 100],

      dialog: null,
      editedItem: null,
      editedIndex: -1,

      errorMessage: '',

      quickSearchID: null,

      searching: false,
      searchParams: {},

      loaderDisplayed: false,
      loaderTitle: null,
      loaderText: null
    }
  },
  mounted () {
    this.setHeaders()
    this.loadData()
  },
  methods: {
    setHeaders () {
      this.headers = this.headers.filter(item => item.countries.includes(this.$country))
    },
    loadData () {
      this.loading = true
      this.searching = false

      let params = {
        page: this.pagination.page - 1,
        pageSize: this.pagination.rowsPerPage
      }

      if (this.searchParams.name) {
        params.name = this.searchParams.name
      }

      this.$store.dispatch('searchCompetitions', params)
        .then(resp => {
          this.pagination.totalItems = resp.totalItems
          this.items = resp.items
          this.loading = false
        })
        .catch(() => {
          alert('Error loading competitions')
          this.loading = false
        })
    },

    search (params) {
      this.pagination = {
        page: 1,
        rowsPerPage: 10,
        totalItems: 0
      }
      this.searchParams = params
      this.loadData()
    },

    clearSearch () {
      this.searchParams = {
        name: null,
        sportID: null,
        type: null
      }
    },

    toggleEdit (index) {
      let editeditem = JSON.parse(JSON.stringify(this.items[index]))
      editeditem.sportID = getSportInfos({ federationId: editeditem.federationID }).id[env]

      this.editedIndex = index
      this.editedItem = editeditem
      this.dialog = true
    },

    del (competition) {
      this.displayLoader({
        title: 'Deleting competition...'
      })
      this.$store.dispatch('deleteCompetition', competition.id)
        .then(() => {
          this.hideLoader()
          this.loadData()
        })
        .catch(() => {
          this.hideLoader()
          alert('Error deleting competition')
        })
    },

    async createCompetition (competition, logo) {
      if (this.checkFields(competition)) {
        return
      }

      if (logo) {
        try {
          competition.logoURL = await this.uploadLogo(logo)
          delete competition.logo
        } catch (error) {
          this.hideLoader()
          alert('Error uploading logo')
          return
        }
      }

      this.displayLoader({
        title: 'Creating competition...'
      })

      this.$store.dispatch('createCompetition', competition)
        .then(resp => {
          this.hideLoader()
          this.showCreatedCompetition(resp.id)
          this.close()
        })
        .catch(() => {
          this.hideLoader()
          alert('Error creating competition')
        })
    },

    async editCompetition (editedCompetition, logo) {
      let competition = JSON.parse(JSON.stringify(editedCompetition))

      if (this.checkFields(competition)) {
        return
      }

      if (logo) {
        try {
          competition.logoURL = await this.uploadLogo(logo)
          delete competition.logo
        } catch (error) {
          this.hideLoader()
          alert('Error uploading logo')
          return
        }
      }

      this.displayLoader({
        title: 'Editing competition...'
      })

      if (!competition.isTournament) {
        delete competition.startDate
        delete competition.endDate
      }

      this.$store.dispatch('patchCompetition', {
        id: this.items[this.editedIndex].id,
        patch: competition
      })
        .then(() => {
          this.hideLoader()
          this.updateItem(this.editedIndex, competition)
          this.close()
        })
        .catch(() => {
          this.hideLoader()
          alert('Error editing competition')
        })
    },

    async uploadLogo (logo) {
      const filename = Date.now().toString() + '-' + logo.fileName

      try {
        await this.$store.dispatch('uploadCompetitionLogo', {
          file: logo.file,
          name: filename
        })

        if (this.$country === 'FR') {
          return `https://rematch-public.b-cdn.net/logos/competitions/${filename}`
        } else {
          return `https://rematch-us-public.b-cdn.net/logos/competitions/${filename}`
        }
      } catch (error) {
        alert('Error uploading logo')
        return null
      }
    },

    showCreatedCompetition (id) {
      this.$store.dispatch('getCompetition', id)
        .then(resp => {
          this.items = [resp]
        })
    },

    updateItem (index, patch) {
      this.items[index].name = patch.name
      this.items[index].federationID = patch.federationID
      this.items[index].regionID = patch.regionID
      this.items[index].categoryID = patch.categoryID
      this.items[index].isTournament = patch.isTournament
      this.items[index].startDate = patch.startDate || null
      this.items[index].endDate = patch.endDate || null
    },

    paginationUpdate (v) {
      if (this.pagination.page !== v.page || this.pagination.rowsPerPage !== v.rowsPerPage) {
        this.pagination = {
          page: v.page,
          rowsPerPage: v.rowsPerPage,
          totalItems: this.pagination.totalItems
        }
        this.loadData()
      }
    },

    checkFields (competition) {
      if (!competition.name) {
        this.errorMessage = 'Please enter a name'
        return true
      }
      if (!competition.federationID) {
        this.errorMessage = 'Please select  a federation'
        return true
      }
      if (!competition.regionID) {
        this.errorMessage = 'Please select a region'
        return true
      }
      if (!competition.categoryID) {
        this.errorMessage = 'Please select a category'
        return true
      }
      if (competition.isTournament) {
        if (!competition.startDate) {
          this.errorMessage = 'Please select a start date'
          return true
        }
        if (!competition.endDate) {
          this.errorMessage = 'Please select an end date'
          return true
        }
      }

      return false
    },

    close () {
      this.dialog = false
      this.editedItem = null
      this.editedIndex = -1
      this.errorMessage = ''
    },

    displayLoader (content) {
      this.loaderTitle = content.title || null
      this.loaderText = content.text || null
      this.loaderDisplayed = true
    },
    hideLoader () {
      this.loaderDisplayed = false
      this.loaderTitle = null
      this.loaderText = null
    }
  },
  computed: {
    dialogState () {
      return this.editedIndex === -1 ? 'create' : 'edit'
    }
  },
  watch: {
    'quickSearchID' () {
      if (this.quickSearchID) {
        if (this.quickSearchID.length !== 20) {
          this.quickSearchID = null
          return
        }
        this.loading = true
        this.$store.dispatch('getCompetition', this.quickSearchID)
          .then(resp => {
            this.items = [resp]
            this.loading = false
          })
          .catch(() => {
            this.loading = false
            alert('Error loading competition')
          })
      } else {
        this.loadData()
      }
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/pages/CompetitionsPage.vue